export const COLS = 10;
export const ROWS = 20;
export const STORAGE_KEY = "family-dashboard-cool-block";

/** @typedef {'I'|'O'|'T'|'S'|'Z'|'J'|'L'} PieceKind */

/** @type {Record<PieceKind, number[][][]>} */
export const SHAPES = {
  I: [
    [[0, 0, 0, 0], [1, 1, 1, 1], [0, 0, 0, 0], [0, 0, 0, 0]],
    [[0, 0, 1, 0], [0, 0, 1, 0], [0, 0, 1, 0], [0, 0, 1, 0]],
    [[0, 0, 0, 0], [0, 0, 0, 0], [1, 1, 1, 1], [0, 0, 0, 0]],
    [[0, 1, 0, 0], [0, 1, 0, 0], [0, 1, 0, 0], [0, 1, 0, 0]]
  ],
  O: [
    [[0, 1, 1, 0], [0, 1, 1, 0], [0, 0, 0, 0], [0, 0, 0, 0]]
  ],
  T: [
    [[0, 1, 0, 0], [1, 1, 1, 0], [0, 0, 0, 0], [0, 0, 0, 0]],
    [[0, 1, 0, 0], [0, 1, 1, 0], [0, 1, 0, 0], [0, 0, 0, 0]],
    [[0, 0, 0, 0], [1, 1, 1, 0], [0, 1, 0, 0], [0, 0, 0, 0]],
    [[0, 1, 0, 0], [1, 1, 0, 0], [0, 1, 0, 0], [0, 0, 0, 0]]
  ],
  S: [
    [[0, 1, 1, 0], [1, 1, 0, 0], [0, 0, 0, 0], [0, 0, 0, 0]],
    [[0, 1, 0, 0], [0, 1, 1, 0], [0, 0, 1, 0], [0, 0, 0, 0]],
    [[0, 0, 0, 0], [0, 1, 1, 0], [1, 1, 0, 0], [0, 0, 0, 0]],
    [[1, 0, 0, 0], [1, 1, 0, 0], [0, 1, 0, 0], [0, 0, 0, 0]]
  ],
  Z: [
    [[1, 1, 0, 0], [0, 1, 1, 0], [0, 0, 0, 0], [0, 0, 0, 0]],
    [[0, 0, 1, 0], [0, 1, 1, 0], [0, 1, 0, 0], [0, 0, 0, 0]],
    [[0, 0, 0, 0], [1, 1, 0, 0], [0, 1, 1, 0], [0, 0, 0, 0]],
    [[0, 1, 0, 0], [1, 1, 0, 0], [1, 0, 0, 0], [0, 0, 0, 0]]
  ],
  J: [
    [[1, 0, 0, 0], [1, 1, 1, 0], [0, 0, 0, 0], [0, 0, 0, 0]],
    [[0, 1, 1, 0], [0, 1, 0, 0], [0, 1, 0, 0], [0, 0, 0, 0]],
    [[0, 0, 0, 0], [1, 1, 1, 0], [0, 0, 1, 0], [0, 0, 0, 0]],
    [[0, 1, 0, 0], [0, 1, 0, 0], [1, 1, 0, 0], [0, 0, 0, 0]]
  ],
  L: [
    [[0, 0, 1, 0], [1, 1, 1, 0], [0, 0, 0, 0], [0, 0, 0, 0]],
    [[0, 1, 0, 0], [0, 1, 0, 0], [0, 1, 1, 0], [0, 0, 0, 0]],
    [[0, 0, 0, 0], [1, 1, 1, 0], [1, 0, 0, 0], [0, 0, 0, 0]],
    [[1, 1, 0, 0], [0, 1, 0, 0], [0, 1, 0, 0], [0, 0, 0, 0]]
  ]
};

export const PIECE_COLORS = {
  I: "#00d4ff",
  O: "#ffd400",
  T: "#b565ff",
  S: "#5ae65a",
  Z: "#ff5a5a",
  J: "#4a7dff",
  L: "#ff9a3c"
};

export const PIECE_NAMES = {
  I: "The Long One",
  O: "The Square Buddy",
  T: "The Tee Rex",
  S: "Snake Left",
  Z: "Snake Right",
  J: "Blue Hook",
  L: "Orange Hook"
};

const KINDS = /** @type {PieceKind[]} */ (Object.keys(SHAPES));

export function emptyBoard() {
  return Array.from({ length: ROWS }, () => Array(COLS).fill(null));
}

export function randomKind() {
  return KINDS[Math.floor(Math.random() * KINDS.length)];
}

export function createPiece(kind = randomKind()) {
  return {
    kind,
    rotation: 0,
    x: Math.floor(COLS / 2) - 2,
    y: 0
  };
}

export function getMatrix(piece) {
  const rotations = SHAPES[piece.kind];
  return rotations[piece.rotation % rotations.length];
}

export function collides(board, piece, offsetX = 0, offsetY = 0, nextRotation = null) {
  const matrix = nextRotation != null
    ? SHAPES[piece.kind][nextRotation % SHAPES[piece.kind].length]
    : getMatrix(piece);
  const px = piece.x + offsetX;
  const py = piece.y + offsetY;

  for (let row = 0; row < 4; row += 1) {
    for (let col = 0; col < 4; col += 1) {
      if (!matrix[row][col]) continue;
      const bx = px + col;
      const by = py + row;
      if (bx < 0 || bx >= COLS || by >= ROWS) return true;
      if (by >= 0 && board[by][bx]) return true;
    }
  }
  return false;
}

export function mergePiece(board, piece) {
  const matrix = getMatrix(piece);
  const next = board.map((row) => [...row]);
  for (let row = 0; row < 4; row += 1) {
    for (let col = 0; col < 4; col += 1) {
      if (!matrix[row][col]) continue;
      const bx = piece.x + col;
      const by = piece.y + row;
      if (by >= 0 && by < ROWS && bx >= 0 && bx < COLS) {
        next[by][bx] = piece.kind;
      }
    }
  }
  return next;
}

export function clearLines(board) {
  const remaining = board.filter((row) => row.some((cell) => cell === null));
  const cleared = ROWS - remaining.length;
  while (remaining.length < ROWS) {
    remaining.unshift(Array(COLS).fill(null));
  }
  return { board: remaining, cleared };
}

export function scoreForLines(lines, level) {
  const table = [0, 100, 300, 500, 800];
  return (table[lines] || 800) * level;
}

export function dropIntervalMs(level) {
  return Math.max(80, 800 - (level - 1) * 70);
}

export function loadHighScore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;
    const data = JSON.parse(raw);
    return Number(data.highScore) || 0;
  } catch {
    return 0;
  }
}

export function saveHighScore(score) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ highScore: score }));
  } catch {
    // ignore quota errors
  }
}

/** Build display grid: locked cells + active piece */
export function buildDisplayGrid(board, piece) {
  const grid = board.map((row) => row.map((cell) => (cell ? { kind: cell, locked: true } : null)));
  if (!piece) return grid;

  const matrix = getMatrix(piece);
  for (let row = 0; row < 4; row += 1) {
    for (let col = 0; col < 4; col += 1) {
      if (!matrix[row][col]) continue;
      const bx = piece.x + col;
      const by = piece.y + row;
      if (by >= 0 && by < ROWS && bx >= 0 && bx < COLS) {
        grid[by][bx] = { kind: piece.kind, locked: false };
      }
    }
  }
  return grid;
}

export function previewGrid(kind, rotation = 0) {
  const matrix = SHAPES[kind][rotation % SHAPES[kind].length];
  const grid = Array.from({ length: 4 }, () => Array(4).fill(null));
  for (let row = 0; row < 4; row += 1) {
    for (let col = 0; col < 4; col += 1) {
      if (matrix[row][col]) grid[row][col] = kind;
    }
  }
  return grid;
}

export function initialGameState() {
  const current = createPiece();
  const next = createPiece();
  return {
    board: emptyBoard(),
    current,
    next,
    score: 0,
    lines: 0,
    level: 1,
    status: "ready",
    highScore: loadHighScore()
  };
}
